import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { Observable, Subject, catchError, distinctUntilChanged, finalize, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { helper } from 'src/environments/helper';
import { LocalitatiService } from './localitati.service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from 'src/app/utils/services/dialog.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import Swal from 'sweetalert2';
import { LocalitateUpdate } from './localitati.interface';
import { AddLocalitatiComponent } from './add-localitati/add-localitati.component';
import { EditLocalitatiComponent } from './edit-localitati/edit-localitati.component';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { Generic } from 'src/app/interfaces/generic.interface';
import { TariService } from '../tari/tari.service';
import { JudeteService } from '../judete/judete.service';
import { FormControl, FormGroup } from '@angular/forms';

@Component({
    selector: 'app-localitati',
    templateUrl: './localitati.component.html',
    styleUrls: ['./localitati.component.scss']
})
export class LocalitatiComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;

    isLoading = false;
    isFilterLoading = false;
    showCard = false;
    activeValue: string = '-1';
    publicValue: string = '-1';

    tari!: Generic[];
    judete!: Generic[];

    searchParamsForm!: FormGroup;

    private destroy$ = new Subject<void>();

    // Filtered fields for search
    filteredTari!: any[];
    tariControl = new FormControl();

    filteredJudete: any[] = [];
    judeteControl = new FormControl();

    //Form Initial Values

    initialFormValues = {
        id_country: 1,
        id_judet: -1,
        localitate: '',
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            id_country: new FormControl(-1),
            id_judet: new FormControl(-1),
            localitate: new FormControl(''),
        });
    }

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    parameters: { key: string; value: string }[] = [
        { key: '--Alegeți--', value: '-1' },
        { key: 'Da', value: '1' },
        { key: 'Nu', value: '0' },
    ];

    defaultParameter: { name: string; id: string } = {
        name: '-- Alegeți --',
        id: '-1',
    };

    resetForm() {
        this.activeValue = '-1';
        this.publicValue = '-1';
        this.searchParamsForm.reset(this.initialFormValues);
        this.loadJudete(this.searchParamsForm.get('id_country')?.value);

        this.filteredTari = this.tari;
        this.tariControl.setValue(null);
        this.judeteControl.setValue(null);

        this.destroy$.next();
        this.destroy$.complete();
        this.renderFieldsSearch();

        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    dtLink =
        environment.interop.basePath +
        environment.interop.platform.nomenclatoare.localitati.getLocalitatiDT;

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private localitatiService: LocalitatiService,
        public permissionsService: PermissionsService,
        private tariService: TariService,
        private judeteService: JudeteService
    ) { }

    ngOnInit(): void {
        this.renderDT();
        this.loadTari();
        this.buildSearchParamsForm();
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();

        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    renderFieldsSearch() {
        this.tariControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredTari = this.tari;
                    return of(this.tari);
                }
                return this.filterTari(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredTari = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.judeteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredJudete = this.judete;
                    return of(this.judete);
                }
                return this.filterJudete(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredJudete = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    renderDT() {
        this.isLoading = true;
        this.dtOptions = {
            pagingType: 'full_numbers',
            responsive: true,
            pageLength: 25,
            searching: true,
            lengthMenu: [10, 25, 50, 100],
            serverSide: true,
            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;
                dataTablesParameters.active = this.activeValue;
                dataTablesParameters.public_visible = this.publicValue;
                dataTablesParameters.filters = this.searchParamsForm.value;

                this.http
                    .post<DataTablesResponse>(
                        this.dtLink, // datatable link, we need this
                        dataTablesParameters // parameters for filtering, send here
                    )
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                },
                {
                    title: 'Localitate',
                    data: 'name',
                },
                {
                    title: 'Județ',
                    data: 'judet_name',
                },
                {
                    title: 'Țară',
                    data: 'country_name',
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    render: (data: any, type: any, row: any, met: any) => {
                        return (
                            `
                  <div class="d-flex align-left justify-content-left">
                                    <button type="button" class="action-btn edit ms-1">
                                        <img class="btn-img" src="/assets/icons/edit.svg" alt="edit"/>
                                    </button>
                                    <button type="button" class="action-btn delete ms-1">
                                        <img class="btn-img" src="/assets/icons/delete.svg" alt="delete"/>
                                    </button>
                                    <button type="button" class="action-btn update_status_active ms-1 ` +
                            (row.active == 1 ? 'action-success' : 'action-danger') +
                            `">
                                        <img class="btn-img" src="` +
                            (row.active == 1
                                ? '/assets/icons/check-white.svg'
                                : '/assets/icons/close-white.svg') +
                            `" alt="update_status_active"/>
                                    </button>
                                    <button type="button" class="action-btn update_status_public ms-1 ` +
                            (row.public_visible == true ? 'action-success' : 'action-danger') +
                            `">
                                        <img class="btn-img" src="` +
                            (row.public_visible == true
                                ? '/assets/icons/public_white.svg'
                                : '/assets/icons/public_not_white.svg') +
                            `" alt="update_status_public"/>
                                    </button>
                                    </div>
                                    `
                        );
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const editIcon = lastTd?.querySelector('.edit') as HTMLElement;
                const deleteIcon = lastTd?.querySelector('.delete') as HTMLElement;
                const updateStatusActiveIcon = lastTd?.querySelector(
                    '.update_status_active'
                ) as HTMLElement;
                const updateStatusPublicIcon = lastTd?.querySelector(
                    '.update_status_public'
                ) as HTMLElement;

                if (editIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.settings.localitati.update'
                        )
                    ) {
                        editIcon.style.display = 'none';
                    }
                    editIcon.addEventListener('click', () => {
                        this.openEditDialog(data);
                    });
                }

                if (deleteIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.settings.localitati.delete'
                        )
                    ) {
                        deleteIcon.style.display = 'none';
                    }
                    deleteIcon.addEventListener('click', () => {
                        const { id } = data;
                        this.deleteLocalitate(id);
                    });
                }

                if (updateStatusActiveIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.settings.localitati.update-status'
                        )
                    ) {
                        updateStatusActiveIcon.style.display = 'none';
                    }
                    updateStatusActiveIcon.addEventListener('click', () => {
                        const { id } = data;
                        this.changeStatusActive(id);
                    });
                }

                if (updateStatusPublicIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.settings.localitati.update-status'
                        )
                    ) {
                        updateStatusActiveIcon.style.display = 'none';
                    }
                    updateStatusPublicIcon.addEventListener('click', () => {
                        const { id } = data;
                        this.changeStatusPublic(id);
                    });
                }

                return row;
            },
        };
    }

    changeStatusActive(id: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'O sa actualizați statusul activ/inactiv aferent înregistrării selectate',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                this.changeStatusActiveAction(id);
            }
        });
    }

    changeStatusActiveAction(id: number) {
        this.isLoading = true;
        this.localitatiService.changeStatusActive(id).subscribe(
            () => {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Înregistrare actualizată cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            (err) => {
                let msg;
                try {
                    // try to catch the message from server
                    msg = err.error.errors.message[0];
                } catch (error) {
                    // controll output message
                    msg = helper.dialogConfig.generalMessages.error;
                }
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.changeStatusActiveAction(id);
                    }
                });
            }
        );
    }

    changeStatusPublic(id: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'O să actualizați statusul funcției selectate!',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                this.changeStatusPublicAction(id);
            }
        });
    }

    changeStatusPublicAction(id: number) {
        this.isLoading = true;
        this.localitatiService.changeStatusPublic(id).subscribe(
            () => {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Funcție actualizată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            (err) => {
                let msg;
                try {
                    // try to catch the message from server
                    msg = err.error.errors.message[0];
                } catch (error) {
                    // controll output message
                    msg = helper.dialogConfig.generalMessages.error;
                }
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.changeStatusPublicAction(id);
                    }
                });
            }
        );
    }

    rerenderDT(resetPaging = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(resetPaging);
        });
        this.showCard = false;
    }

    openAddDialog() {
        const dialogRef = this.dialog.open(AddLocalitatiComponent, {
            ...this.dialogService.getDialogConfig(),
            data: this.tari
        });
        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Adăugată cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    openEditDialog(data: LocalitateUpdate) {
        const dialogRef = this.dialog.open(EditLocalitatiComponent, {
            ...this.dialogService.getDialogConfig(),
            data: {
                data: data,
                tari: this.tari
            },
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Actualizare cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    deleteLocalitate(id: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'Nu o să puteți recupera datele odată cu ștergerea lor!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                // we do this because we need to have some control on activity
                this.deleteAction(id);
            }
        });
    }

    deleteAction(id: number) {
        this.isLoading = true;
        this.localitatiService.deleteLocalitate(id).subscribe(
            () => {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Înregistrarea ștearsă cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            (err) => {
                let msg;
                try {
                    // try to catch the message from server
                    msg = err.error.errors.message[0];
                } catch (error) {
                    // controll output message
                    msg = helper.dialogConfig.generalMessages.error;
                }

                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.deleteAction(id);
                    }
                });
            }
        );
    }

    loadTari() {
        this.isFilterLoading = true;
        this.tariService.getTariActive()
            .pipe(
                finalize(() => this.isFilterLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.tari = results;
                    this.filteredTari = [...this.tari];
                    this.searchParamsForm.get('id_country')?.patchValue(1);
                    this.searchParamsForm.get('id_country')?.disable();
                    this.loadJudete(this.searchParamsForm.get('id_country')?.value);
                },
                error: (error) => {
                    console.error('Error in get tari: ', error);
                }
            })
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterTari(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.tari.filter(tara =>
            this.normalizeString(tara.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onTaraSelectClosed() {
        const selectedValue = this.tariControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredTari.some(tara =>
                tara.id === this.searchParamsForm.get('id_country')?.value
            );

            if (!isSelectedInFilteredList) {
                this.tariControl.setValue(null);
                this.filteredTari = this.tari;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onTaraChange(taraId: number): void {
        this.searchParamsForm.controls['id_judet'].patchValue(-1);

        if (taraId == -1) {
            this.judete = [];
            this.filteredJudete = [];
            return
        }

        if (taraId) {
            this.loadJudete(taraId);
        }
    }

    loadJudete(taraId: number) {
        this.isFilterLoading = true;

        let judeteObservable$ = taraId
            ? this.judeteService.getJudeteActiveByTari(taraId)
            : this.judeteService.getJudeteActive();

        judeteObservable$.pipe(
            map((results: any) => [...results.data]),
            finalize(() => {
                this.isFilterLoading = false;
            })
        ).subscribe({
            next: (judete) => {
                this.judete = [...judete];
                this.filteredJudete = [...judete];
            },
            error: (err) => {
                console.log(err);
                this.filteredJudete = [];
            }
        });
    }

    onJudeteSelectClosed() {
        const selectedValue = this.judeteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredJudete.some(judet =>
                judet.id === this.searchParamsForm.get('id_judet')?.value
            );

            if (!isSelectedInFilteredList) {
                this.judeteControl.setValue(null);
                this.filteredJudete = this.judete;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    filterJudete(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.judete.filter(judet =>
            this.normalizeString(judet.name).includes(normalizedSearchTerm)
        ));
    }
}
